/**
 * @file: PlanDetailGroupCard.tsx
 *
 * One order's card in the plans timeline: the order header, then its detail
 * rows on a shared vertical rail (reusing the feed `TimelineItem`). Per-row
 * edit / cancel bubble up so the screen can patch or drop the row in the
 * infinite list. Editability is the shared `canEditPlanDetail` rule (not yet
 * a lead, not past its service date).
 */

import { TimelineItem } from '@euler/app/flows/feed/components/TimelineItem';
import { PlanDetailEditPatch } from '@euler/app/flows/feed/components/message-body/common/PlanDetailActionsButton';
import { abnormalLevelInfo } from '@euler/app/flows/feed/components/message-body/common/labels';
import { canEditPlanDetail } from '@euler/app/flows/feed/components/message-body/common/planDetailEditable';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, memo } from 'react';
import { View } from 'react-native';
import { PlanDetailGroup } from '../functions/groupPlanDetails';
import { PlanDetailItemBody } from './PlanDetailItemBody';
import { PlanGroupHeader } from './PlanGroupHeader';

export const PlanDetailGroupCard: FC<{
  group: PlanDetailGroup;
  onItemEdited: (detailId: number, patch: PlanDetailEditPatch) => void;
  onItemRemoved: (detailId: number) => void;
  onItemAlertChanged: (
    detailId: number,
    settings: { isAlertEnabled: boolean; isAlertEnabledForCustomer: boolean },
  ) => void;
}> = memo(({ group, onItemEdited, onItemRemoved, onItemAlertChanged }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      css={`
        background-color: #fff;
      `}
    >
      <PlanGroupHeader item={group.header} />
      <View
        css={`
          padding: ${rpx(10)}px ${rpx(4)}px ${rpx(6)}px ${rpx(12)}px;
        `}
      >
        {group.items.map((item, i) => {
          const editable = canEditPlanDetail(item);
          return (
            <TimelineItem
              key={item.id}
              accent={abnormalLevelInfo(item.abnormalLevel).color}
              isFirst={i === 0}
              isLast={i === group.items.length - 1}
            >
              <PlanDetailItemBody
                item={item}
                editable={editable}
                onEdited={patch => onItemEdited(item.id, patch)}
                onRemoved={() => onItemRemoved(item.id)}
                onAlertChanged={settings =>
                  onItemAlertChanged(item.id, settings)
                }
              />
            </TimelineItem>
          );
        })}
      </View>
    </View>
  );
});
